import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashHeader } from "@/components/account";
import { VerifyBanner } from "@/components/VerifyBanner";
import { useSeo } from "@/lib/seo";
import "@/styles/account.css";

const STATE_LABEL = {
  created: "Awaiting payment", awaiting_submission: "Awaiting details", planning: "Planning",
  dispatching: "Finding testers", running: "Testing", verifying: "Verifying",
  awaiting_release: "In review", delivered: "Delivered", cancelled: "Cancelled",
};

function jobLink(j) {
  if (!j.status_token) return null;
  return j.report_available ? `/report/${j.status_token}` : `/status/${j.status_token}`;
}

export default function Dashboard() {
  useSeo({ title: "Your dashboard", path: "/dashboard", noindex: true });
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { replace: true }); return; }
    api.accountJobs().then(setData).catch(() => setErr(true));
  }, [user, loading, navigate]);

  if (loading || (!data && !err)) {
    return (<><DashHeader /><div className="dash-wrap"><p className="muted">Loading…</p></div></>);
  }
  if (err) {
    return (<><DashHeader /><div className="dash-wrap"><p className="muted">Could not load your dashboard. Please try again.</p></div></>);
  }

  const jobs = data.jobs || [];
  const paidJobs = jobs.filter((j) => j.paid);
  const credits = data.credits || { INR: 0, USD: 0 };
  const ledger = data.ledger || [];

  return (
    <>
      <DashHeader />
      <div className="dash-wrap" data-testid="buyer-dashboard">
        <VerifyBanner />
        <div className="dash-head">
          <h1>Hi {user.name || user.email}</h1>
          <p className="muted">Everything you've ordered since creating this account.</p>
        </div>

        <div className="dash-cards">
          <div className="dash-card" data-testid="card-jobs"><span className="k">Jobs</span><b>{jobs.length}</b></div>
          <div className="dash-card" data-testid="card-delivered"><span className="k">Delivered</span><b>{jobs.filter((j) => j.state === "delivered").length}</b></div>
          <div className="dash-card" data-testid="card-credit-inr"><span className="k">Credit (INR)</span><b>{money(credits.INR || 0, "INR")}</b></div>
          <div className="dash-card" data-testid="card-credit-usd"><span className="k">Credit (USD)</span><b>{money(credits.USD || 0, "USD")}</b></div>
        </div>

        <section className="dash-sec">
          <div className="dash-sec-head"><h2>Jobs &amp; reports</h2><a className="btn sm blue" href="/pricing" data-testid="dash-new-job">Start a new job</a></div>
          {jobs.length === 0 ? (
            <p className="muted" data-testid="jobs-empty">No jobs yet. Your orders will appear here once you place them while signed in.</p>
          ) : (
            <div className="dash-table-wrap">
              <table className="dash-table" data-testid="jobs-table">
                <thead><tr><th>Job</th><th>Package</th><th>Status</th><th>Placed</th><th>Report</th></tr></thead>
                <tbody>
                  {jobs.map((j) => {
                    const link = jobLink(j);
                    return (
                      <tr key={j.id} data-testid={`job-row-${j.code}`}>
                        <td><b>{j.code}</b>{j.rerun_of && <span className="tag">re-run of {j.rerun_of}</span>}</td>
                        <td>{j.package}</td>
                        <td><span className="state">{STATE_LABEL[j.state] || j.state}</span>{j.sessions_total ? <span className="muted small"> · {j.sessions_done}/{j.sessions_total} sessions</span> : null}</td>
                        <td className="muted small">{j.created_at ? new Date(j.created_at).toLocaleDateString() : "—"}</td>
                        <td>{link ? <a className="link" href={link} data-testid={`job-link-${j.code}`}>{j.report_available ? "View report" : "Open"}</a> : <span className="muted">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="dash-sec">
          <div className="dash-sec-head"><h2>Invoices &amp; payments</h2></div>
          {paidJobs.length === 0 ? (
            <p className="muted" data-testid="invoices-empty">No payments yet.</p>
          ) : (
            <div className="dash-table-wrap">
              <table className="dash-table" data-testid="invoices-table">
                <thead><tr><th>Job</th><th>Amount</th><th>Method</th><th>Date</th></tr></thead>
                <tbody>
                  {paidJobs.map((j) => (
                    <tr key={j.id} data-testid={`invoice-row-${j.code}`}>
                      <td><b>{j.code}</b></td>
                      <td>{money(j.amount_minor || 0, j.currency)}{j.credit_used_minor ? <span className="muted small"> · {money(j.credit_used_minor, j.currency)} credit</span> : null}</td>
                      <td>{j.paid_via === "credit" ? "Credit" : "Card"}</td>
                      <td className="muted small">{j.delivered_at ? new Date(j.delivered_at).toLocaleDateString() : (j.created_at ? new Date(j.created_at).toLocaleDateString() : "—")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {ledger.length > 0 && (
          <section className="dash-sec">
            <div className="dash-sec-head"><h2>Credit activity</h2></div>
            <div className="dash-table-wrap">
              <table className="dash-table" data-testid="credit-ledger">
                <thead><tr><th>When</th><th>Reason</th><th>Job</th><th>Amount</th></tr></thead>
                <tbody>
                  {ledger.map((e, i) => (
                    <tr key={i}>
                      <td className="muted small">{e.at ? new Date(e.at).toLocaleDateString() : "—"}</td>
                      <td>{e.reason || "—"}</td>
                      <td className="muted small">{e.job_code || "—"}</td>
                      <td className={e.amount_minor < 0 ? "neg" : "pos"}>{money(e.amount_minor || 0, e.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
